<template>
    <div>
        <div>
            <input
                v-for="field in hiddenFields"
                :key="field.uid"
                type="hidden"
                :name="field.name"
                :value="field.model"
            />
        </div>
        <div class="tw-grid tw-gap-x-6 tw-gap-y-0 tw-grid-cols-12">
            <div
                v-for="field in visibleFields" 
                :key="field.uid" 
                class="tw-col-span-12"
                :class="matchCols[field.cols]"
            >
                    <!-- ====== Text Field ====== -->
                    <template v-if="field.field_type == 'input'">
                        <v-text-field 
                            v-model="field.model" 
                            :label="field.label" 
                            :name="field.name"
                            :rules="getFieldRules(field)" 
                            type="text"
                            v-bind="commonProps()"
                        />
                    </template>
                    <!-- ====== Text Area ====== -->
                    <template v-if="field.field_type == 'textarea'">
                        <v-textarea 
                            v-model="field.model" 
                            :label="field.label" 
                            :rules="getFieldRules(field)" 
                            :name="field.name" 
                            v-bind="commonProps()"
                        />
                    </template>
                    <!-- ====== Radio Buttons ====== -->
                    <template v-if="field.field_type == 'radio'">
                        <v-radio-group 
                            v-model="field.model" 
                            class="tw-mt-0" 
                            :label="field.label" 
                            :rules="getFieldRules(field)" 
                        >
                            <v-radio 
                                v-for="option in field.options" 
                                :key="option.uid" 
                                :label="option.text"
                                :value="option.value"
                            />
                        </v-radio-group>
                    </template>
                    <!-- ====== Checkbox ====== -->
                    <template v-if="field.field_type == 'checkbox'">
                        <div>
                            <label for="first_name" class="tw-block tw-text-sm tw-font-medium tw-text-slate-600">
                                {{ field.label }}
                            </label>
                            <v-checkbox 
                                v-for="option in field.options" 
                                :key="option.uid"
                                v-model="field.model_multiple" 
                                :rules="getFieldRules(field, true)" 
                                :label="option.text" 
                                :value="option.value"
                                hide-details 
                            />
                        </div>
                    </template>
                    <!-- ====== Select ====== -->
                    <template
                        v-if="field.field_type == 'select' && field.is_multiple
                        && field.label != 'Departments'
                        && field.label != 'Provinces'
                        && field.label != 'Districts'"
                    >
                        <v-select
                            v-model="field.model_multiple" 
                            :label="field.label" 
                            :rules="getFieldRules(field, true)" 
                            :items="optionsFor(field)"
                            clearable
                            multiple
                            v-bind="commonProps()"
                        >
                            <template #item="data">
                                <div v-html="formatText(data.item.text)" />
                            </template>
                            <template #selection="{ item, index }">
                                <div v-if="index === 0" v-html="formatText(item.text)" />
                                <span v-if="index === 1" class="grey--text text-caption tw-ml-1">
                                    ({{ field.model_multiple.length - 1 }} {{ field.model_multiple.length - 1 === 1 ? $t('app.more_selection') : $t('app.more_selections') }})
                                </span>
                            </template>
                        </v-select>
                    </template>
                    <template
                        v-if="field.field_type == 'select' 
                        && !field.is_multiple
                        && field.label != 'Departments'
                        && field.label != 'Provinces'
                        && field.label != 'Districts'"
                    >
                        <v-select
                            v-model="field.model" 
                            :label="field.label" 
                            :rules="getFieldRules(field)" 
                            :items="optionsFor(field)"
                            clearable
                            v-bind="commonProps()"
                        >
                            <template #item="data">
                                <div v-html="formatText(data.item.text)" />
                            </template>
                            <template #selection="data">
                                <div v-html="formatText(data.item.text)" />
                            </template>
                        </v-select>
                    </template>
                    <!-- ====== Number ====== -->
                    <template v-if="field.field_type == 'number'">
                        <v-text-field 
                            v-model="field.model" 
                            :label="field.label" 
                            :name="field.name"
                            :rules="getFieldRules(field)"
                            :counter="!!field.characters" 
                            :maxlength="field.characters"
                            v-bind="commonProps()"
                            @keypress="onlyNumber"
                        />
                    </template>
                    <!-- ====== Date ====== -->
                    <template v-if="field.field_type == 'date'">
                        <v-text-field 
                            v-model="field.model" 
                            :label="field.label" 
                            :name="field.name"
                            :rules="getFieldRules(field)" 
                            type="date" 
                            v-bind="commonProps()"
                        />
                    </template>
                    <!-- ====== Departments ====== -->
                    <template v-if="field.field_type == 'select' && field.label == 'Departments'">
                        <DepartmentsAutocomplete 
                            v-model="field.model"
                            :rules="getFieldRules(field)" 
                            v-bind="commonProps()"
                            @change="listProvinceByDepartments" 
                        />
                    </template>
                    <!-- ====== Provinces ====== -->
                    <template v-if="field.field_type == 'select' && field.label == 'Provinces'">
                        <ProvincesAutocomplete 
                            v-model="field.model" 
                            :search="itemsProvinces"
                            :rules="getFieldRules(field)" 
                            v-bind="commonProps()"
                            @change="listDistricByProvinces"
                        />
                    </template>
                    <!-- ====== Districts ====== -->
                    <template v-if="field.field_type == 'select' && field.label == 'Districts'">
                        <DistrictsAutocomplete 
                            v-model="field.model" 
                            :search="itemsDistritcs"
                            :rules="getFieldRules(field)" 
                            v-bind="commonProps()"
                        />
                    </template>
                    <!-- ====== Upload ====== -->
                    <template v-if="field.field_type == 'upload'">
                        <v-file-input
                            v-model="field.model" 
                            :label="field.label" 
                            :name="field.name"
                            :rules="getFieldRules(field)" 
                            show-size
                            truncate-length="15"
                            v-bind="commonProps()"
                        />
                    </template>
                    <!-- ====== Representatives ====== -->
                    <!-- <template v-if="field.field_type == 'domain'">
                        <UsersAutocomplete
                            v-model="field.model"
                            :label="field.label"
                            :name="field.name"
                            :rules="field.is_required ? [rules.required] : []"
                            :multiple="false"
                            :use-notes="true"
                            filter-name="representative"
                            hide-details
                        />
                    </template> -->
            </div>
        </div>
    </div>
</template>

<script>
import { matchCols } from '@/utils/formCols'
import { rules } from '@/utils/rules'
import { onlyNumber } from '@/utils/Settings'
import DepartmentsAutocomplete from '@/components/DepartmentsAutocomplete'
import ProvincesAutocomplete from '@/components/ProvincesAutocomplete'
import DistrictsAutocomplete from '@/components/DistrictsAutocomplete'

export default {
    name: 'DynamicFields',
    components: {
        DepartmentsAutocomplete,
        ProvincesAutocomplete,
        DistrictsAutocomplete,
    },
    props: {
        fields: {
            type: [Array, Object],
            required: true,
        },
    },
    data: () => ({
        matchCols,
        rules,
        onlyNumber,
        itemsProvinces: null,
        itemsDistritcs: null,
    }),
    computed: {
        commonProps() {
            return () => {
                return {
                    outlined: true,
                    validateOnBlur: true,
                    required: true,
                    rounded: true,
                    hideDetails: false,
                }
            }
        },
        hiddenFields() {
            return this.filteredFields.filter(f =>
                f.hasVisibility && f.visibility.mode === 'hidden',
            )
        },
        visibleFields() {
            return this.filteredFields.filter(f =>
                !(f.hasVisibility && f.visibility.mode === 'hidden'),
            )
        },
        filteredFields() {
            return this.fields.filter(field => {
                if (!field.hasVisibility) return true
                const { mode, field: fk, value } = field.visibility
                if (mode === 'always' || mode === 'hidden') return true
                if (mode === 'depends_on') {
                    const parent = this.fields.find(f => f.name === fk)
                    if (!parent) return true
                    if (value) {
                        return parent.model === value
                    }
                    return !!parent.model
                }
                return true
            })
        },

    },
    watch: {
        fields: {
            handler(newFields) {
                newFields.forEach(f => {
                    if (f.hasVisibility && f.visibility.mode === 'hidden' && f.defaultMapping?.length) {
                        const parent = newFields.find(p => p.name === f.visibility.field)
                        const pv = parent?.model
                        const rule = f.defaultMapping.find(m => m.parentValue === pv)
                        if (rule) {
                            f.model = rule.model
                        }    
                    }
                })
            },
            deep: true,
        },
    },
    methods: {
        listProvinceByDepartments(e) {
            this.itemsProvinces = e
        },
        listDistricByProvinces(e) {
            this.itemsDistritcs = e
        },
        formatText(text) {
            const regex = /\*(.*?)\*/g
            return text.replace(regex, '<strong>$1</strong>')
        },
        optionsFor(field) {
            const vis = field.visibility || {}

            if (vis.mode !== 'depends_on') return field.options
            
            const parent = this.fields.find(f => f.name === vis.field)
            const parentVal = parent ? parent.model : null

            if (vis.value) return field.options

            if (!parentVal) return []

            return field.options.filter(opt => opt.visibleWhen === parentVal)
        },
        getFieldRules(field, isArray = false) {
            if (!field.rules || !field.rules.length) return field.is_required ? (isArray ? [this.rules.array_not_empty] : [this.rules.required]) : []
            
            const fieldRules = []
            
            field.rules.forEach(validation => {
                const { type, param, dependentField, valueRules } = validation
                
                if (type === 'required' && field.is_required) {
                    fieldRules.push(isArray ? this.rules.array_not_empty : this.rules.required)
                    return
                }

                if (type === 'dependent' && dependentField && valueRules?.length) {
                    const parentField = this.fields.find(f => f.name === dependentField)

                    if (!parentField) return

                    const parentValue = parentField.model

                    const matchingRules = valueRules.filter(vr => vr.parentValue === parentValue)

                    matchingRules.forEach(ruleDef => {
                        const { ruleType, param, message } = ruleDef

                        if (ruleType && this.rules[ruleType]) {
                            const rule = this.rules[ruleType](param)
                            if (message) {
                                rule.message = message
                            }
                            fieldRules.push(rule)
                        }
                    })
                }
                
                if (!this.rules[type]) return
           
                switch(type) {
                    case 'min':
                    case 'max':
                    case 'startsWith':
                        if (param !== undefined) {
                            fieldRules.push(this.rules[type](param))
                        }
                        break
                    default:
                        fieldRules.push(this.rules[type])
                        break
                }
            })
            
            return fieldRules
        },
    },
}
</script>